import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { imageUrl, srcSet } from '../lib/images';
import { fetchCollection } from '../lib/cms';
import SEO from '../components/SEO';
import TrustSlider from '../components/TrustSlider';
import ContactForm from '../components/ContactForm';
import SparklePattern from '../components/SparklePattern';
import Lightbox from '../components/Lightbox';

export function Home() {
  const [services, setServices] = useState([]);
  const [lightboxIndex, setLightboxIndex] = useState(-1);

  const galleryImages = [
    {
      url: '/_assets/img-2044.jpeg',
      billede: ['/_assets/img-2044.jpeg'],
      titel: 'Samsung EcoBubble Vaskemaskine',
      tekst: 'Fejlfinding og udskiftning af reservedele under servicebesøg',
      alt: 'Hvid Samsung vaskemaskine under reparation med reservedel ovenpå',
      tag: 'Reparation',
    },
    {
      url: '/_assets/img-0212.jpeg',
      billede: ['/_assets/img-0212.jpeg'],
      titel: 'ASKO Frontbetjent Vaskemaskine',
      tekst: 'Montering, testkørsel og gennemgang af betjening',
      alt: 'Hvid ASKO frontbetjent vaskemaskine med manual på toppen',
      tag: 'Installation',
    },
    {
      url: '/_assets/img-1991.jpeg',
      billede: ['/_assets/img-1991.jpeg'],
      titel: 'Køkken med Køleskab & Ovn',
      tekst: 'Eftersyn af integreret køl/frys og moderne indbygningsovn',
      alt: 'Moderne køkken med indbygningsovn og køleskab i rustfrit stål',
      tag: 'Køl & Ovn',
    },
    {
      url: '/_assets/img-2053.jpeg',
      billede: ['/_assets/img-2053.jpeg'],
      titel: 'Samsung Vaskemaskine i Bryggers',
      tekst: 'Korrekt tilslutning og nivellering i niche',
      alt: 'Hvid Samsung vaskemaskine installeret i en niche på gråt klinkegulv',
      tag: 'Montering',
    },
    {
      url: '/_assets/img-1866.jpeg',
      billede: ['/_assets/img-1866.jpeg'],
      titel: 'Køleanlæg & Trykmåling',
      tekst: 'Diagnostik med manometer og påfyldning af R600a kølemiddel',
      alt: 'Manometer med rød slange tilkoblet køleanlæg under serviceeftersyn',
      tag: 'Fejlfinding',
    },
  ];

  useEffect(() => {
    fetchCollection('services').then((data) => {
      if (data && data.length > 0) {
        setServices(data);
      }
    });
  }, []);

  const brands = [
    'Bosch',
    'Siemens',
    'Miele',
    'Electrolux',
    'Whirlpool',
    'Samsung',
    'LG',
    'AEG',
    'Gram',
    'Voss',
  ];

  return (
    <>
      <SEO
        title="MA Hvidevareservice"
        description="MA Hvidevareservice - Din lokale hvidevareservice med holdninger. Hurtig og pålidelig service og reparation af dine hvidevarer."
      />

      {/* Hero Section */}
      <section className="relative bg-[#38b6ff] border-b border-[#229ee6] overflow-hidden isolate">
        {/* Ambient Ethereal Sparkle Outlines (Design Reference Style) */}
        <div className="absolute inset-0 pointer-events-none -z-10 overflow-hidden" aria-hidden="true">
          {/* Subtle soft gradient glow */}
          <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-white/10 blur-3xl" />
          <div className="absolute -bottom-32 -left-32 w-96 h-96 rounded-full bg-white/10 blur-3xl" />

          {/* Large wireframe sparkle - top right behind image */}
          <svg
            className="absolute -top-16 -right-16 w-80 h-80 sm:w-96 sm:h-96 lg:w-[460px] lg:h-[460px] stroke-white/20 stroke-[1.2] fill-none -rotate-6 transition-transform duration-1000"
            viewBox="0 0 100 100"
          >
            <path d="M 50 5 C 52 28, 72 48, 95 50 C 72 52, 52 72, 50 95 C 48 72, 28 52, 5 50 C 28 48, 48 28, 50 5 Z" />
          </svg>

          {/* Medium wireframe sparkle - top left */}
          <svg
            className="absolute -top-10 -left-10 w-48 h-48 sm:w-64 sm:h-64 lg:w-80 lg:h-80 stroke-white/15 stroke-[1.25] fill-none rotate-12"
            viewBox="0 0 100 100"
          >
            <path d="M 50 5 C 52 28, 72 48, 95 50 C 72 52, 52 72, 50 95 C 48 72, 28 52, 5 50 C 28 48, 48 28, 50 5 Z" />
          </svg>

          {/* Delicate floating sparkle - center left */}
          <svg
            className="absolute top-1/2 left-4 sm:left-12 w-14 h-14 sm:w-20 sm:h-20 stroke-white/25 stroke-[1.5] fill-none -rotate-12"
            viewBox="0 0 100 100"
          >
            <path d="M 50 5 C 52 28, 72 48, 95 50 C 72 52, 52 72, 50 95 C 48 72, 28 52, 5 50 C 28 48, 48 28, 50 5 Z" />
          </svg>

          {/* Floating accent sparkle - bottom center */}
          <svg
            className="absolute -bottom-8 left-1/3 sm:left-1/2 w-24 h-24 sm:w-32 sm:h-32 stroke-white/15 stroke-[1.2] fill-none rotate-45"
            viewBox="0 0 100 100"
          >
            <path d="M 50 5 C 52 28, 72 48, 95 50 C 72 52, 52 72, 50 95 C 48 72, 28 52, 5 50 C 28 48, 48 28, 50 5 Z" />
          </svg>

          {/* Subtle tiny accent sparkle - bottom right */}
          <svg
            className="absolute bottom-6 right-12 lg:right-24 w-12 h-12 sm:w-16 sm:h-16 stroke-white/20 stroke-[1.5] fill-none rotate-6"
            viewBox="0 0 100 100"
          >
            <path d="M 50 5 C 52 28, 72 48, 95 50 C 72 52, 52 72, 50 95 C 48 72, 28 52, 5 50 C 28 48, 48 28, 50 5 Z" />
          </svg>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
            {/* Left Content */}
            <div className="lg:col-span-7 space-y-6">
              <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-black text-slate-950 tracking-tight leading-[1.1]">
                Din lokale hvidevareservice <br className="hidden sm:inline" />
                <span className="text-slate-900">med holdninger</span>
              </h1>

              <p className="text-base sm:text-lg text-slate-900 font-medium leading-relaxed max-w-2xl">
                Jeg hedder Mikki, og jeg startede MA Hvidevareservice, fordi jeg er træt af at se gode maskiner blive smidt ud, bare fordi en lille del er slidt eller en fejl kan løses på en time.
              </p>

              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 pt-2">
                <Link
                  to="/kontakt"
                  className="inline-flex items-center justify-center px-7 py-3.5 bg-slate-950 hover:bg-slate-900 text-white font-bold text-sm rounded-lg transition-colors shadow-md"
                >
                  <span>Kontakt os</span>
                </Link>

                <a
                  href="tel:69306445"
                  className="inline-flex items-center justify-center px-6 py-3.5 bg-white hover:bg-slate-50 text-slate-950 border border-white font-bold text-sm rounded-lg transition-colors whitespace-nowrap shadow-sm"
                  aria-label="Ring til 69 30 64 45"
                >
                  <span className="whitespace-nowrap">69 30 64 45</span>
                </a>
              </div>
            </div>

            {/* Right Hero Image */}
            <div className="lg:col-span-5 relative">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl aspect-[4/3] lg:aspect-[5/4] bg-slate-100">
                <img
                  src={imageUrl('/_assets/imgi-17-730313792-122104141370438737-3804474252068835818-n.jpeg', 'width=1000,quality=85,format=auto')}
                  srcSet={srcSet('/_assets/imgi-17-730313792-122104141370438737-3804474252068835818-n.jpeg', [400, 800, 1200])}
                  sizes="(max-width: 1024px) 100vw, 500px"
                  alt="MA Hvidevareservice firmabil og servicevogn"
                  className="w-full h-full object-cover rounded-2xl"
                  loading="eager"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Brand Strips - Mærker vi servicerer */}
      <section className="hidden md:block bg-slate-50 border-b border-slate-200/70 py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <span className="text-xs font-bold uppercase tracking-wider text-slate-500 whitespace-nowrap">
              Reparation på alle kendte mærker:
            </span>
            <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm font-semibold text-slate-700">
              {brands.map((b) => (
                <span key={b}>
                  {b}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Introduction & Philosophy Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-start">
            <div>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-[#53b7ff] tracking-tight leading-tight">
                Kvalitetsarbejde frem for hurtige halve løsninger
              </h2>
            </div>

            <div className="text-slate-600 leading-relaxed text-sm sm:text-base space-y-4">
              <p>
                Hos mig handler det ikke om at sælge dig den hurtigste løsning eller tjene mest muligt på en tur. Det handler om at få din vaskemaskine, opvaskemaskine, tørretumbler, køleskab eller ovn til at køre 100 % perfekt igen – eller sige ærligt til dig, hvis det faktisk ikke kan betale sig at reparere.
              </p>
              <p>
                Jeg kører først videre, når maskinen virker som den skal. Punktum. Ingen halve løsninger, ingen midlertidige fiksfakserier. Faglig stolthed betyder meget for mig.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid Section */}
      <section className="relative py-16 sm:py-20 bg-[#38b6ff] border-t border-b border-[#229ee6] overflow-hidden isolate">
        <SparklePattern variant="white" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-4">
            <div>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black text-slate-950 tracking-tight">
                Serviceydelser
              </h2>
            </div>
            <Link
              to="/service-priser"
              className="inline-flex items-center text-sm font-bold text-slate-950 hover:text-slate-900 bg-white/90 hover:bg-white px-4 py-2 rounded-lg shadow-sm transition-colors"
            >
              <span>Se alle priser og vilkår</span>
              <i className="fa-solid fa-arrow-right ml-2 text-xs" aria-hidden="true" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {services.map((item, index) => {
              const numStr = String(index + 1).padStart(2, '0');
              return (
                <Link
                  key={item.id || item.service_key}
                  to={`/ydelser/${item.service_key}`}
                  className="group bg-white border border-slate-200/80 rounded-xl overflow-hidden shadow-sm flex flex-col justify-between block p-6"
                >
                  <div className="grid grid-cols-[auto_1fr] gap-6 sm:gap-7">
                    {/* Kolonne 1: Nummer i toppen, SVG i bunden */}
                    <div className="flex flex-col justify-between items-start py-0.5">
                      <span className="text-sm font-black text-slate-400 group-hover:text-[#38b6ff] transition-colors tracking-wider font-mono">
                        {numStr}
                      </span>
                      <svg
                        className="w-4 h-4 text-[#38b6ff] shrink-0 mt-4"
                        viewBox="0 0 100 100"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="7"
                        strokeLinejoin="round"
                        strokeLinecap="round"
                        aria-hidden="true"
                      >
                        <path d="M 50 5 C 52 28, 72 48, 95 50 C 72 52, 52 72, 50 95 C 48 72, 28 52, 5 50 C 28 48, 48 28, 50 5 Z" />
                      </svg>
                    </div>

                    {/* Kolonne 2: Titel og beskrivelse */}
                    <div>
                      <h3 className="text-lg font-bold text-slate-900 mb-2">
                        {item.title}
                      </h3>
                      <p className="text-sm text-slate-600 leading-relaxed mb-4">
                        {item.short_desc}
                      </p>
                      <span className="text-xs font-bold text-sky-600 group-hover:underline inline-block">
                        Læs mere
                      </span>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Proces: Sådan foregår det */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-12">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-[#53b7ff] tracking-tight">
              Sådan foregår et servicebesøg
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="relative">
              <span className="block text-4xl sm:text-5xl font-black text-[#53b7ff] mb-2 leading-none">
                1
              </span>
              <h3 className="text-lg font-bold text-slate-900 mb-2">
                Udkørsel & Fejlfinding
              </h3>
              <p className="text-sm text-slate-600 leading-relaxed">
                Servicebesøg og grundig fejlfinding koster <strong>995 kr. inkl. moms</strong>. Kan maskinen fikses uden reservedele inden for 30 minutter, er dette den samlede pris. Du kontaktes altid 20 minutter før ankomst.
              </p>
            </div>

            <div className="relative">
              <span className="block text-4xl sm:text-5xl font-black text-[#53b7ff] mb-2 leading-none">
                2
              </span>
              <h3 className="text-lg font-bold text-slate-900 mb-2">
                Altid gratis prisoverslag
              </h3>
              <p className="text-sm text-slate-600 leading-relaxed">
                Jeg laver ALTID et mundtligt eller skriftligt overslag, før noget repareres. Du beslutter helt selv, om vi går videre. Kan det ikke betale sig at reparere, siger jeg det ærligt.
              </p>
            </div>

            <div className="relative">
              <span className="block text-4xl sm:text-5xl font-black text-[#53b7ff] mb-2 leading-none">
                3
              </span>
              <h3 className="text-lg font-bold text-slate-900 mb-2">
                1–2 års garanti & ren maskine
              </h3>
              <p className="text-sm text-slate-600 leading-relaxed">
                Vi har ofte reservedele med i bilen til hurtig reparation ved første besøg. Du får 1–2 års garanti på arbejde og nye dele, og maskinen efterlades altid ren og pæn.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Billeder fra hverdagen / Udført arbejde */}
      <section className="py-16 sm:py-20 bg-slate-50 border-t border-slate-200/80">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Grid med de 5 billeder */}
          <div className="grid grid-cols-1 sm:grid-cols-1 md:grid-cols-1 lg:grid-cols-12 gap-4 sm:gap-6">
            {/* Billede 1: Stor venstre (col-span-7) */}
            <div
              onClick={() => setLightboxIndex(0)}
              className="lg:col-span-7 group relative rounded-2xl overflow-hidden bg-slate-900 border border-slate-200/80 shadow-xs hover:shadow-md transition-all duration-300 cursor-pointer h-72 sm:h-80 lg:h-96"
            >
              <img
                src={imageUrl(galleryImages[0].url, 'width=1000,quality=85,format=auto')}
                srcSet={srcSet(galleryImages[0].url, [500, 800, 1100])}
                sizes="(min-width: 1024px) 58vw, 100vw"
                alt={galleryImages[0].alt}
                loading="lazy"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
            </div>

            {/* Billede 2: Højre øverst (col-span-5) */}
            <div
              onClick={() => setLightboxIndex(1)}
              className="lg:col-span-5 group relative rounded-2xl overflow-hidden bg-slate-900 border border-slate-200/80 shadow-xs hover:shadow-md transition-all duration-300 cursor-pointer h-72 sm:h-80 lg:h-96"
            >
              <img
                src={imageUrl(galleryImages[1].url, 'width=800,quality=85,format=auto')}
                srcSet={srcSet(galleryImages[1].url, [400, 700, 900])}
                sizes="(min-width: 1024px) 42vw, 100vw"
                alt={galleryImages[1].alt}
                loading="lazy"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
            </div>

            {/* Billede 3: Bund venstre (col-span-4) */}
            <div
              onClick={() => setLightboxIndex(2)}
              className="lg:col-span-4 group relative rounded-2xl overflow-hidden bg-slate-900 border border-slate-200/80 shadow-xs hover:shadow-md transition-all duration-300 cursor-pointer aspect-[4/3]"
            >
              <img
                src={imageUrl(galleryImages[2].url, 'width=700,quality=85,format=auto')}
                srcSet={srcSet(galleryImages[2].url, [350, 600, 800])}
                sizes="(min-width: 1024px) 33vw, 100vw"
                alt={galleryImages[2].alt}
                loading="lazy"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
            </div>

            {/* Billede 4: Bund midt (col-span-4) */}
            <div
              onClick={() => setLightboxIndex(3)}
              className="lg:col-span-4 group relative rounded-2xl overflow-hidden bg-slate-900 border border-slate-200/80 shadow-xs hover:shadow-md transition-all duration-300 cursor-pointer aspect-[4/3]"
            >
              <img
                src={imageUrl(galleryImages[3].url, 'width=700,quality=85,format=auto')}
                srcSet={srcSet(galleryImages[3].url, [350, 600, 800])}
                sizes="(min-width: 1024px) 33vw, 100vw"
                alt={galleryImages[3].alt}
                loading="lazy"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
            </div>

            {/* Billede 5: Bund højre (col-span-4) */}
            <div
              onClick={() => setLightboxIndex(4)}
              className="lg:col-span-4 group relative rounded-2xl overflow-hidden bg-slate-900 border border-slate-200/80 shadow-xs hover:shadow-md transition-all duration-300 cursor-pointer aspect-[4/3]"
            >
              <img
                src={imageUrl(galleryImages[4].url, 'width=700,quality=85,format=auto')}
                srcSet={srcSet(galleryImages[4].url, [350, 600, 800])}
                sizes="(min-width: 1024px) 33vw, 100vw"
                alt={galleryImages[4].alt}
                loading="lazy"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Infinite Slider: Værdier & Kundeløfter */}
      <TrustSlider />

      {/* Contact Form & Info Section */}
      <section className="py-16 sm:py-24 bg-slate-50 border-t border-slate-200/80" id="kontakt-sektion">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-start">
            {/* Left Info Column */}
            <div className="lg:col-span-5 space-y-6">
              <div>
                <h2 className="text-2xl sm:text-3xl font-extrabold text-[#53b7ff] tracking-tight">
                  Klar til at få fikset din hvidevare?
                </h2>
                <p className="text-sm text-slate-600 mt-3 leading-relaxed">
                  Ring direkte eller send en forespørgsel i formularen. Vi vender hurtigt tilbage for at aftale tid til et servicebesøg.
                </p>
              </div>

              <div className="space-y-4 py-2">
                <div className="flex items-center gap-3">
                  <div className="shrink-0 text-sky-600">
                    <i className="fa-solid fa-phone text-base" aria-hidden="true" />
                  </div>
                  <div>
                    <a
                      href="tel:69306445"
                      className="text-base font-bold text-slate-900 hover:text-sky-600 transition-colors whitespace-nowrap"
                    >
                      69 30 64 45
                    </a>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <div className="shrink-0 text-sky-600">
                    <i className="fa-solid fa-envelope text-base" aria-hidden="true" />
                  </div>
                  <div>
                    <a
                      href="mailto:mahvidevareservice@gmail.com"
                      className="text-sm font-semibold text-slate-900 hover:text-sky-600 transition-colors whitespace-nowrap break-all sm:break-normal"
                    >
                      mahvidevareservice@gmail.com
                    </a>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <div className="shrink-0 text-sky-600">
                    <i className="fa-solid fa-location-dot text-base" aria-hidden="true" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-slate-900">
                      Ruten 75, 1. th., 2700 Brønshøj
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <div>
                    <div className="text-sm font-semibold text-slate-900">
                      CVR: 46308409
                    </div>
                  </div>
                </div>
              </div>

              {/* Servicefradrag callout */}
              <div className="p-4 bg-sky-50 border border-sky-200/70 rounded-xl text-xs text-sky-900 leading-relaxed">
                <span className="font-bold block mb-1">
                  <i className="fa-solid fa-coins mr-1.5" aria-hidden="true" />
                  Husk servicefradrag via SKAT:
                </span>
                Du kan få ca. 26% tilbage i skat på arbejdslønnen (op til loftet på ca. 18.300 kr. pr. person i 2026). Vi hjælper gerne med dokumentation til din selvangivelse.
              </div>
            </div>

            {/* Right Form Column */}
            <div className="lg:col-span-7">
              <ContactForm
                title="Send en forespørgsel"
                subtitle="Beskriv problemet med din maskine, så vender vi hurtigt tilbage."
              />
            </div>
          </div>
        </div>
      </section>

      {/* Lightbox Modal */}
      {lightboxIndex >= 0 && (
        <Lightbox
          images={galleryImages}
          currentIndex={lightboxIndex}
          onClose={() => setLightboxIndex(-1)}
          onNavigate={(newIdx) => setLightboxIndex(newIdx)}
        />
      )}
    </>
  );
}

export default Home;
